/* eslint-disable react/sort-comp */
// Lib
import React from 'react';
import PropTypes from 'prop-types';

// Utils
import { isAttemptingMultiSelect } from '../../element/selection/selectionUtils';
import { getRelativePositionFromDomEvent } from '../../utils/dom/domEventUtil';
import { getRelativePositionFromLongPressEvent } from '../../utils/dom/longPressEventUtil';

import * as pointLib from '../../../common/maths/geometry/point';
import { hasChanged } from '../../utils/react/propsComparisons';

const eventListenerNodeHasChanged = hasChanged('eventListenerNode');

/**
 * Attaches the DOM event listeners and invokes the state update methods as appropriate based on long-press and
 * touch moves.
 * This is very similar to the selectionMarqueeMouseEventHandlerDecorator.
 */
export default (DecoratedComponent) => {
    class SelectionMarqueeLongPressEventHandler extends React.Component {
        constructor(props) {
            super(props);
            this.eventsAttached = false;
        }

        componentWillMount() {
            this.attachMarqueeSelectionStartListener(this.props);
        }

        componentDidMount() {
            this.attachMarqueeSelectionStartListener(this.props);
        }

        componentDidUpdate(oldProps) {
            // Remove the event listeners and then re-attach them if we're changing the event listener node
            if (eventListenerNodeHasChanged(oldProps, this.props)) {
                this.removeEventListeners(oldProps);
            }

            this.attachMarqueeSelectionStartListener(this.props);
        }

        componentWillUnmount() {
            this.removeEventListeners(this.props);
        }

        attachMarqueeSelectionStartListener = (props) => {
            if (!props.eventListenerNode || this.eventsAttached) return;

            this.eventsAttached = true;

            props.eventListenerNode.addEventListener('long-press', this.onLongPress);
        };

        removeMarqueeSelectionStartListener = (props) => {
            props.eventListenerNode.removeEventListener('long-press', this.onLongPress);
        };

        attachMarqueeSelectionMoveListeners = () => {
            document.addEventListener('touchmove', this.onTouchMove, { passive: false });
            document.addEventListener('touchend', this.onTouchEnd, { passive: false });
            document.addEventListener('touchcancel', this.onTouchCancel, { passive: false });
        };

        removeMarqueeSelectionMoveListeners = () => {
            document.removeEventListener('touchmove', this.onTouchMove, { passive: false });
            document.removeEventListener('touchend', this.onTouchEnd, { passive: false });
        };

        removeEventListeners = (props) => {
            if (!props.eventListenerNode) return;
            this.eventsAttached = false;

            this.removeMarqueeSelectionStartListener(props);
            this.removeMarqueeSelectionMoveListeners(props);
        };

        onLongPress = (event) => {
            const { startMarqueeSelect, eventListenerNode, canvasDocumentNode } = this.props;

            const eventTargetIsEmptyCanvas = event.target === eventListenerNode || event.target === canvasDocumentNode;

            if (!eventTargetIsEmptyCanvas) return;

            // Set focus to the document body so that keyboard shortcuts can be used again after interacting
            // with an iFrame
            window.focus();
            if (document.activeElement) document.activeElement.blur();

            event.preventDefault();

            this.attachMarqueeSelectionMoveListeners();

            const appendMode = isAttemptingMultiSelect(event);
            const eventPosition = this.getEventPosition(event, getRelativePositionFromLongPressEvent);

            startMarqueeSelect({ ...eventPosition, appendMode });
        };

        onTouchMove = (event) => {
            // Need to prevent scrolls from occurring
            event.preventDefault();
            event.stopPropagation();

            const eventPosition = this.getEventPosition(event);
            this.props.dragMarqueeSelectionTo(eventPosition);
        };

        onTouchEnd = (event) => {
            this.props.finishMarqueeSelect();
            this.removeMarqueeSelectionMoveListeners(this.props);
        };

        onTouchCancel = (event) => {
            this.props.finishMarqueeSelect();
        };

        getEventPosition = (event, getterFn = getRelativePositionFromDomEvent) => {
            const { offset, gridSize } = this.props;

            const scaledOffset = offset ? pointLib.scale(gridSize, offset) : null;

            return scaledOffset
                ? pointLib.translate(scaledOffset, getterFn(event, this.props.eventListenerNode))
                : getterFn(event, this.props.eventListenerNode);
        };

        render() {
            return <DecoratedComponent {...this.props} />;
        }
    }

    SelectionMarqueeLongPressEventHandler.propTypes = {
        eventListenerNode: PropTypes.object,
        canvasDocumentNode: PropTypes.object,
        startMarqueeSelect: PropTypes.func,
        dragMarqueeSelectionTo: PropTypes.func,
        finishMarqueeSelect: PropTypes.func,
        dispatchDeselectAllElements: PropTypes.func,
        deselectAll: PropTypes.func,

        offset: PropTypes.object,
        gridSize: PropTypes.number,
    };

    return SelectionMarqueeLongPressEventHandler;
};
